import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatButtonModule } from '@angular/material/button';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { CookieModule } from 'ngx-cookie';
import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor';
import { NgIdleModule } from '@ng-idle/core';
import {
  NgcCookieConsentModule,
  NgcCookieConsentConfig,
} from 'ngx-cookieconsent';
import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import { AuthModule } from '@auth0/auth0-angular';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LaunchDarklyService } from './core/services/launchdarkly.service';
import { RuntimeConfigService } from './core/services/runtime-config.service';
import { SecurityService } from './core/services/security.service';

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain:'',
  },
  position: 'top',
  theme: 'classic',
  palette: {
    popup: {
      background: '#4f4d57',
      text: '#ffffff',
      link: '#ffffff',
    },
    button: {
      background: '#fece18',
      text: '#000000',
      border: 'transparent',
    },
  },
  type: 'info',
  layout: 'custom-layout',
  layouts: {
    'custom-layout': '{{messagelink}}{{compliance}}',
  },
  elements: {
    messagelink: `
    <span id="cookieconsent:desc" class="cc-message" style="font-family: 'Azo Sans' !important'">{{message}} 
      <a aria-label="Learn more about our privacy policy" tabindex="1" class="cc-link" style="color: rgb(0, 153, 255);" href="{{privacyPolicyHref}}" target="_blank">{{privacyPolicyLink}}</a> and our 
      <a aria-label="Learn more about our terms of service" tabindex="2" class="cc-link" style="color: rgb(0, 153, 255);" href="{{tosHref}}" target="_blank">{{tosLink}}</a>.
    </span>
    `,
  },
  content: {
    message: 'For more information about our cookie policy, please see our ',
    privacyPolicyLink: 'Privacy Policy',
    privacyPolicyHref: `/privacy-policy.html`,

    tosLink: 'Terms of Service',
    tosHref: `/term-services.html`,
    dismiss: 'Got it!',

    policy: 'Cookie Policy',
  },
};

const monacoConfig: NgxMonacoEditorConfig = {
  baseUrl: './assets', // configure base path for monaco editor default: './assets'
  defaultOptions: { scrollBeyondLastLine: false }, // pass default options to be used
  onMonacoLoad: () => {
    const monaco = (<any>window).monaco;
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      noLib: true,
      allowNonTsExtensions: true,
    });
  }, // here monaco object will be available as window.monaco use this function to extend monaco editor functionalities.
};

export function initializeSecurity(securityService: SecurityService) {
  return () => securityService.loadPublicKey();  // Must load before app boots
}

export function initializeRuntimeConfig(runtimeConfig: RuntimeConfigService, ldService: LaunchDarklyService) {
  return async () => {
    await ldService.initialize();  // No-op if already initialized; ensures flag values are available
    await runtimeConfig.loadFromApi();
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument(),
    EffectsModule.forRoot([]),
    SharedModule,
    CoreModule,
    NgbModule,
    ToastrModule.forRoot({
      preventDuplicates: true,
    }),
    CookieModule.forRoot(),
    MonacoEditorModule.forRoot(monacoConfig),
    NgIdleModule.forRoot(),
    NgcCookieConsentModule.forRoot(cookieConfig),
    AuthModule.forRoot({
      domain: environment.Auth0Domain,
      clientId: environment.Auth0ClientId,
      authorizationParams: {
        redirect_uri: window.location.origin + '/callback-handler',
        audience: environment.Auth0Audience,
        scope: 'openid profile email'
      },
      useRefreshTokens: true,
      useRefreshTokensFallback: false,
      cacheLocation: 'localstorage',
      skipRedirectCallback: true
    }),
  ],
  providers: [
    DatePipe,
    LaunchDarklyService,
    {
      provide: APP_INITIALIZER,
      useFactory: (launchDarklyService: LaunchDarklyService) => () => launchDarklyService.initialize(),
      deps: [LaunchDarklyService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSecurity,
      deps: [SecurityService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRuntimeConfig,
      deps: [RuntimeConfigService, LaunchDarklyService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
